import { useState, useEffect } from 'react';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  loginUrl: string | null;
  userId: string | null;
}

// Module-level caches so the synchronous accessors can return values
let _cachedToken: string | null = null;
let _cachedUserId: string | null = null;

/**
 * Extract the auth token and user ID from an ident-format string.
 * The ident format uses query-string format: a=<JWT>&u=<user_id>&n=<name>
 */
function parseIdentString(ident: string): { token: string | null; userId: string | null } {
  try {
    // Parse manually instead of URLSearchParams to avoid URL-decoding the JWT
    const tokenMatch = ident.match(/(?:^|&)a=([^&]*)/);
    const userIdMatch = ident.match(/(?:^|&)u=([^&]*)/);
    return {
      token: tokenMatch ? tokenMatch[1] : null,
      userId: userIdMatch ? userIdMatch[1] : null,
    };
  } catch {
    return { token: null, userId: null };
  }
}

/**
 * Extract the auth token and user ID from Hudl's "ident" cookie.
 */
function getTokenAndUserIdFromIdentCookie(): { token: string | null; userId: string | null } {
  try {
    const identCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('ident='));
    if (!identCookie) return { token: null, userId: null };

    const cookieValue = identCookie.substring('ident='.length);
    return parseIdentString(cookieValue);
  } catch {
    return { token: null, userId: null };
  }
}

/**
 * Check if a JWT token is still valid (not expired).
 * Decodes the payload without verifying the signature
 * (signature verification happens server-side on Hudl's API).
 */
function isTokenValid(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // Decode the payload (base64url -> JSON)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(decoded);

    const exp = claims.exp;
    if (!exp) return false;

    // Allow 60 seconds of clock skew
    return Date.now() / 1000 < exp + 60;
  } catch {
    return false;
  }
}

/**
 * Determine the login URL base.
 * In AgentCore mode, use the configured login URL.
 * Otherwise, derive from the GraphQL endpoint or fall back to hudl.com.
 */
function getLoginBaseUrl(): string {
  const configuredUrl = import.meta.env.VITE_LOGIN_BASE_URL;
  if (configuredUrl) return configuredUrl;
  return 'https://master.thorhudl.com';
}

/**
 * Fetch the auth token and user ID from Hudl's ident API.
 * The browser sends the ident cookie (domain: www.hudl.com) with the request
 * because the destination is www.hudl.com (same-site). This works even when
 * basketball-ai.hudl.com can't read the cookie directly via document.cookie.
 */
async function fetchTokenAndUserIdFromIdentApi(): Promise<{ token: string | null; userId: string | null }> {
  try {
    const url = `${getLoginBaseUrl()}/api/v2/authorizations/ident`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) return { token: null, userId: null };

    const data = await response.json();
    const ident: string | undefined = data?.ident;
    if (!ident) return { token: null, userId: null };

    return parseIdentString(ident);
  } catch {
    return { token: null, userId: null };
  }
}

/**
 * Synchronous accessor for the cached auth token.
 * Returns the cached token if still valid, otherwise tries the cookie.
 * Used by useChat.ts to attach the token to API requests.
 */
export function getAuthToken(): string | null {
  if (_cachedToken && isTokenValid(_cachedToken)) {
    return _cachedToken;
  }
  // Try refreshing from cookie (fast, synchronous)
  const { token: cookieToken, userId } = getTokenAndUserIdFromIdentCookie();
  if (cookieToken && isTokenValid(cookieToken)) {
    _cachedToken = cookieToken;
    if (userId) _cachedUserId = userId;
    return _cachedToken;
  }
  // Return whatever is cached (may be from ident API fetch)
  return _cachedToken;
}

/**
 * Synchronous accessor for the cached user ID.
 */
export function getUserId(): string | null {
  return _cachedUserId;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    loginUrl: null,
    userId: null,
  });

  useEffect(() => {
    const loginBaseUrl = getLoginBaseUrl();
    const loginUrl = `${loginBaseUrl}/login?forward=${encodeURIComponent(window.location.origin)}`;

    // Localhost bypass for local development
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      setState({ isAuthenticated: true, isLoading: false, loginUrl, userId: null });
      return;
    }

    // Fast path: try reading the cookie directly
    const { token: cookieToken, userId } = getTokenAndUserIdFromIdentCookie();
    if (cookieToken && isTokenValid(cookieToken)) {
      _cachedToken = cookieToken;
      if (userId) _cachedUserId = userId;
      setState({ isAuthenticated: true, isLoading: false, loginUrl, userId });
      return;
    }

    // Slow path: cookie not readable (e.g. basketball-ai.hudl.com),
    // fall back to fetching from the ident API
    fetchTokenAndUserIdFromIdentApi().then(({ token: apiToken, userId: apiUserId }) => {
      if (apiToken && isTokenValid(apiToken)) {
        _cachedToken = apiToken;
        if (apiUserId) _cachedUserId = apiUserId;
        setState({ isAuthenticated: true, isLoading: false, loginUrl, userId: apiUserId });
      } else {
        setState({ isAuthenticated: false, isLoading: false, loginUrl, userId: null });
      }
    });
  }, []);

  return state;
}
